import React, { useEffect, useMemo, useState } from "react";
import {
  FormSectionElements,
  FormSections,
  ProgramContainer,
} from "../../entities/formTemplateDataType";
import FormValidationUtils from "../../utilities/FormValidationUtils";
import axios from "axios";
import DataUtils from "../../utilities/DataUtils";
import ReCaptchaV3 from "../captcha/CaptchComponent";
import ReCaptchaButton from "../captcha/captcha-button";
// import { gaPageview } from '../../utilities/GAUtils';
import { APP_APPLICATION_TYPE_MAP } from "../../entities/Constants";
import SectionUtils from "../../utilities/SectionUtils";
import { getEnvVar } from "../../config/RuntimeConfig";
import TrackTaskCompletion, {
  getSubmissionId,
} from "../../utilities/TrackTaskCompletion";

interface Props {
  token: string;
  programContainer: ProgramContainer;
  isEng: boolean;
  section: FormSections;
  onButtonClick: (data) => void;
  callbackStepIndex?: (index: number, returnIndex?: number) => void;
}

function base64ToFile(base64, filename, mimeType) {
  const byteCharacters = atob(base64);
  const byteNumbers = Array.from(byteCharacters).map((c) => c.charCodeAt(0));
  const byteArray = new Uint8Array(byteNumbers);
  const blob = new Blob([byteArray], { type: mimeType });
  return new File([blob], encodeURI(filename), { type: mimeType });
}

const APPHandleSubmitComponent: React.FC<Props> = ({
  programContainer,
  isEng,
  section,
  onButtonClick,
  callbackStepIndex,
}) => {
  const [validationErrMsg, setValidationErrMsg] = useState<any[]>([]);
  const [isBusy, setIsBusy] = useState<boolean>(false);
  const [confirmationVisible, setConfirmationVisible] =
    useState<boolean>(false);
  const [summaryVisible, setSummaryVisible] = useState<boolean>(true);
  const [submissionError, setSubmissionError] = useState<boolean>(false);
  const [transactionId, setTransactionId] = useState<string>("");
  const [applicationType, setApplicationType] = useState<string>("");
  const [showDownloadSummary, setShowDownloadSummary] = useState<boolean>(true);
  const [renewType, setRenewType] = useState<string>("");

  const cleanupAfterSubmit = () => {
    if (!programContainer.ClearFormAfterSubmit) {
      return;
    }
    DataUtils.clearFormValue(programContainer);
    SectionUtils.resetDirtyFlags(programContainer);
  };

  const handleSubmitNoRecaptha = () => {
    handleSubmit(null);
  };

  const updateApplicationType = (metadata) => {
    let res: string;

    if (metadata.renewReplaceOrFirstTime === "Renew") {
      if (metadata.renewType === "Existing") {
        const digit = metadata.identificationNumber[11];
        if (digit == "1") {
          res = "RWR";
        } else if (digit == "2") {
          res = "STC";
        } else if (digit == "3") {
          res = "TMP";
        }
      } else if (metadata.renewType !== "Existing") {
        res = APP_APPLICATION_TYPE_MAP[metadata.renewType];
      }
    } else if (metadata.renewReplaceOrFirstTime === "Replace") {
      res = metadata.isStolen ? "STL" : "LST";
    } else if (metadata.renewReplaceOrFirstTime === "FirstTime") {
      res = "NEW";
    }

    setApplicationType(res);
  };

  const handleSubmit = (token: string) => {
    const invalidControlList = FormValidationUtils.invalidControls(section);
    if (invalidControlList.length > 0) {
      setValidationErrMsg(invalidControlList);
      window.scrollTo(0, 0);
    } else {
      let url = programContainer.UpdateAPI;
      const data = DataUtils.collectFormValue(programContainer);
      const updateToken = programContainer.Profile?.token;
      const headers = {};

      if (updateToken) {
        headers["Authorization"] = `Bearer ${updateToken}`;
      }

      const PayloadValues = {
        "Yes, I currently have an accessible parking permit": "Renew",
        "I need to replace a lost or stolen permit": "Replace",
        "No, this is the first time I’m applying for an accessible parking permit":
          "FirstTime",
        myself: "Myself",
        "my child": "Mychild",
        "legal authorized representative": "MyClient",
        "Renew existing permit": "Existing",
        "Request a traveller's permit": "Traveller",
        "Request a motorcycle decal": "Motorcycle_decal",
      };
      const FileToMimeType = {
        pdf: "application/pdf",
        jpeg: "image/jpeg",
        png: "image/png",
        doc: "application/msword",
      };

      const formData = new FormData();

      const metadata = {
        language: isEng ? "EN" : "FR",
        userId: programContainer.Profile?.userId,
        token: data.token,
        renewReplaceOrFirstTime:
          PayloadValues[
            data.Do__you__currently__have__an__accessible__parking__permit
          ],
        firstName: data.First__name,
        middleName: data.Middle__name,
        lastName: data.Last__name,
        dateOfBirth: data.Date__of__birth.split("/")
          .map((d) => (d.length < 2 ? `0${d}` : d))
          .join("/"),
        gender:
          data.Gender === "Mâle"
            ? "Male"
            : data.Gender === "Femelle"
              ? "Female"
              : data.Gender,
        primaryPhoneNumber: data.Primary__phone__number.replace(/[^\d]/g, ""),
        emailAddress: data.Email__address,
        confirmEmailAddress: data.Confirm__email__address,
        hasStreetNumberOrName:
          data.Address__search.lotType === undefined ||
          data.Address__search.lotType === "",
      };
      if (
        data.What__is__the__driver__status__of__the__permit__holder__new ||
        data.What__is__the__driver__status__of__the__permit__holder__renewal
      ) {
        if (
          (data.What__is__the__driver__status__of__the__permit__holder__new &&
            ["Passenger", "Passager"].includes(
              data.What__is__the__driver__status__of__the__permit__holder__new,
            )) ||
          (data.What__is__the__driver__status__of__the__permit__holder__renewal &&
            ["Passenger", "Passager"].includes(
              data.What__is__the__driver__status__of__the__permit__holder__renewal,
            ))
        ) {
          metadata["isPassengerOnly"] = true;
        } else {
          metadata["isPassengerOnly"] = false;
        }
      }

      if (data.Who__is__this__application__for) {
        metadata["applicationFor"] =
          data.Who__is__this__application__for !== null
            ? PayloadValues[
                data.Who__is__this__application__for.split("-")[0]
                  .slice(0, -1)
                  .toLowerCase()
              ]
            : null;
      }
      if (data.I__am__completing__this__form__on__behalf__of) {
        metadata["applicationFor"] =
          data.I__am__completing__this__form__on__behalf__of !== null
            ? PayloadValues[
                data.I__am__completing__this__form__on__behalf__of.toLowerCase()
              ]
            : null;
      }
      if (data["Was__your__permit:"]) {
        metadata["isStolen"] =
          data["Was__your__permit:"] === "Stolen" ||
          data["Was__your__permit:"] === "volé";
      }
      if (data.Occurrence__number) {
        metadata["occurrenceNumber"] = data.Occurrence__number;
      }
      if (data.Name__of__police__service) {
        metadata["policeService"] = data.Name__of__police__service;
      }
      if (data.Drivers__licence__number) {
        metadata["driverLicenceNumber"] =
          data.Drivers__licence__number !== null
            ? data.Drivers__licence__number.replace(/-/g, "")
            : null;
      }
      if (data.Permit__number) {
        metadata["permitNumber"] = data.Permit__number;
      }
      if (data.Identification__number) {
        metadata["identificationNumber"] = data.Identification__number;
      }

      if (data.What__would__you__like__to__do__Select__one) {
        metadata["renewType"] =
          PayloadValues[data.What__would__you__like__to__do__Select__one];
        setRenewType(metadata["renewType"]);
      }

      if (data.Permit__holders__motorcycle__licence__plate__number) {
        metadata["motorLicPlateNumber"] =
          data.Permit__holders__motorcycle__licence__plate__number;
      }

      if (data.Permit__holders__motorcycle__VIN__number) {
        metadata["motorcycleVinNumber"] =
          data.Permit__holders__motorcycle__VIN__number;
      }

      if (data.Attachment__description) {
        let descriptions = [];
        data.Attachment__description.forEach((attachmentObj) => {
          descriptions.push(attachmentObj.description);
          formData.append(
            "files",
            base64ToFile(
              attachmentObj.attachment.split(",")[1],
              attachmentObj.path,
              FileToMimeType[
                attachmentObj.path.split(".").at(-1).toLowerCase()
              ],
            ),
          );
        });
        metadata["fileDescriptions"] = descriptions;
      }

      const residentialAddress = data["Address__search"];
      metadata["residentialAddress"] = {
        postalCode: residentialAddress.postalCode,
        streetNum: residentialAddress.streetNumber.concat(
          residentialAddress.streetNumberSuffix
            ? ` ${residentialAddress.streetNumberSuffix}`
            : "",
        ),
        streetName: residentialAddress.streetName.concat(
          ` ${residentialAddress.streetType}`,
        ),
        unitNum: residentialAddress.suiteNumber,
        city: residentialAddress.municipality,
        province: residentialAddress.province,
        poBox: residentialAddress.poBoxNumber,
      };

      const mailingAddress = data["Enter__your__mailing__address"];
      metadata["mailingAddress"] = {
        postalCode: mailingAddress.postalCode,
        streetNum: mailingAddress.streetNumber.concat(
          mailingAddress.streetNumberSuffix
            ? ` ${mailingAddress.streetNumberSuffix}`
            : "",
        ),
        streetName: mailingAddress.streetName.concat(
          ` ${mailingAddress.streetType}`,
        ),
        unitNum: mailingAddress.suiteNumber,
        city: mailingAddress.municipality,
        province: mailingAddress.province,
        poBox: mailingAddress.poBoxNumber,
      };

      metadata["mailingAddrSameAsResidential"] =
        JSON.stringify(metadata["residentialAddress"]) ===
        JSON.stringify(metadata["mailingAddress"]);

      if (getEnvVar("DISABLED_RECAPTCHA") == "false") {
        metadata["token"] = token;
      }

      setShowDownloadSummary(
        !["Traveller", "Motorcycle_decal"].includes(metadata["renewType"]),
      );

      updateApplicationType(metadata);

      metadata.userId = programContainer.Profile?.userId || "";
      console.log("metadata is ", metadata);
      setIsBusy(true);
      formData.append(
        "metadata",
        new File([JSON.stringify(metadata)], "metadata.json", {
          type: "application/json",
        }),
      );
      axios
        .post(url, formData)
        .then((res) => {
          console.log(res);
          setIsBusy(false);
          setConfirmationVisible(true);
          setSummaryVisible(false);
          setTransactionId(res.data.uploaderToOPSResult.transactionCode);

          const appActionMap: Record<string, "new" | "renewal" | "update"> = {
            Renew: "renewal",
            Replace: "update",
            FirstTime: "new",
          };
          TrackTaskCompletion(programContainer, {
            serviceName: "accessible parking permit",
            serviceItems: "accessible parking permit",
            serviceAction:
              appActionMap[metadata.renewReplaceOrFirstTime] || "new",
            value: "0.00",
            submissionId: getSubmissionId("app"),
          });

          DataUtils.clearDraft(
            programContainer.CategoryId,
            programContainer.Profile?.email,
            programContainer.draftId,
          );
          programContainer.draftId = undefined;
          cleanupAfterSubmit();
          // const title = { title: 'Submit' };
          // gaPageview(title)
        })
        .catch((err) => {
          setIsBusy(false);
          setSummaryVisible(false);
          setSubmissionError(true);
          console.log(err);
        });
    }
  };

  const props = useMemo(
    () => ({
      isBusy,
      confirmationVisible,
      summaryVisible,
      submissionError,
      validationErrMsg,
      transactionId,
      showDownloadSummary,
      applicationType,
      renewType,
    }),
    [isBusy, validationErrMsg, transactionId, showDownloadSummary, renewType],
  );

  useEffect(() => {
    onButtonClick(props);
  }, [props]);

  return (
    <div className="ontario-margin-top-24-!">
      {getEnvVar("DISABLED_RECAPTCHA") == "false" && (
        <ReCaptchaV3 lang={isEng ? "en" : "fr"}>
          <ReCaptchaButton
            key={"recaptchbutton"}
            title={isEng ? "Submit" : "Soumettre"}
            action="submitAPP"
            buttonHandler={handleSubmit}
          >
            {isEng ? "Submit" : "Soumettre"}
          </ReCaptchaButton>
        </ReCaptchaV3>
      )}
      {getEnvVar("DISABLED_RECAPTCHA") == "true" && (
        <button
          onClick={handleSubmitNoRecaptha}
          className="ontario-button ontario-button--primary"
        >
          {isEng ? "Submit" : "Soumettre"}
        </button>
      )}
    </div>
  );
};

export default APPHandleSubmitComponent;
